import type {
  ExtrusionLayerState,
  GeometryPiece,
  StoreyRenderSlice,
  WireframeLayerState,
  WireframePieceSlice,
} from './types';

type PackedVertices = {
  vertices: Float32Array;
  vertexCount: number;
};

/**
 * Append two packed vertex buffers. Empty sides return the other buffer as-is.
 */
export const concatPackedVertices = (
  first: PackedVertices,
  second: PackedVertices,
): PackedVertices => {
  if (!second.vertexCount) return first;
  if (!first.vertexCount) return second;
  const vertices = new Float32Array(
    first.vertices.length + second.vertices.length,
  );
  vertices.set(first.vertices);
  vertices.set(second.vertices, first.vertices.length);
  return {
    vertices,
    vertexCount: first.vertexCount + second.vertexCount,
  };
};

type VertexOffsets = {
  floor: number;
  wall: number;
  roof: number;
};

/**
 * Shift {@link GeometryPiece.first} so sibling slices draw after the selected
 * building's vertices in the same GPU buffers.
 */
const offsetExtrusionSlices = (
  slices: StoreyRenderSlice[],
  offsets: VertexOffsets,
): StoreyRenderSlice[] =>
  slices.map((slice) => ({
    ...slice,
    pieces: slice.pieces.map((piece): GeometryPiece => ({
      ...piece,
      first: piece.first + offsets[piece.buffer],
    })),
  }));

/**
 * Join selected-building extrusion with cached sibling geometry.
 * Sibling {@link StoreyRenderSlice} indices are offset onto the selected buffers.
 */
export const concatExtrusionGeometries = (
  selected: ExtrusionLayerState,
  sibling: ExtrusionLayerState,
): ExtrusionLayerState => {
  if (
    !sibling.vertexCount &&
    !sibling.wallVertexCount &&
    !sibling.roofVertexCount
  ) {
    return selected;
  }
  const floors = concatPackedVertices(
    { vertices: selected.vertices, vertexCount: selected.vertexCount },
    { vertices: sibling.vertices, vertexCount: sibling.vertexCount },
  );
  const walls = concatPackedVertices(
    {
      vertices: selected.wallVertices,
      vertexCount: selected.wallVertexCount,
    },
    { vertices: sibling.wallVertices, vertexCount: sibling.wallVertexCount },
  );
  const roofs = concatPackedVertices(
    {
      vertices: selected.roofVertices,
      vertexCount: selected.roofVertexCount,
    },
    { vertices: sibling.roofVertices, vertexCount: sibling.roofVertexCount },
  );
  return {
    vertices: floors.vertices,
    vertexCount: floors.vertexCount,
    wallVertices: walls.vertices,
    wallVertexCount: walls.vertexCount,
    roofVertices: roofs.vertices,
    roofVertexCount: roofs.vertexCount,
    storeySlices: [
      ...selected.storeySlices,
      ...offsetExtrusionSlices(sibling.storeySlices, {
        floor: selected.vertexCount,
        wall: selected.wallVertexCount,
        roof: selected.roofVertexCount,
      }),
    ],
  };
};

const offsetWireframeIndex = (
  index: number | undefined,
  vertexOffset: number,
): number | undefined =>
  index === undefined ? undefined : index + vertexOffset;

/**
 * Join selected-building wireframe with cached sibling line geometry.
 * Overlay vertices stay on the selected building (draft / selection).
 */
export const concatWireframeGeometries = (
  selected: WireframeLayerState,
  sibling: WireframeLayerState,
): WireframeLayerState => {
  if (!sibling.pointCount) {
    return selected;
  }
  const lines = concatPackedVertices(
    { vertices: selected.vertices, vertexCount: selected.pointCount },
    { vertices: sibling.vertices, vertexCount: sibling.pointCount },
  );
  const overlay = concatPackedVertices(
    {
      vertices: selected.overlayVertices,
      vertexCount: selected.overlayPointCount,
    },
    {
      vertices: sibling.overlayVertices,
      vertexCount: sibling.overlayPointCount,
    },
  );
  const vertexOffset = selected.pointCount;
  return {
    vertices: lines.vertices,
    pointCount: lines.vertexCount,
    overlayVertices: overlay.vertices,
    overlayPointCount: overlay.vertexCount,
    storeySlices: [
      ...selected.storeySlices,
      ...sibling.storeySlices.map((slice) => ({
        ...slice,
        first: slice.first + vertexOffset,
      })),
    ],
    pieceSlices: [
      ...selected.pieceSlices,
      ...sibling.pieceSlices.map((slice): WireframePieceSlice => ({
        ...slice,
        first: slice.first + vertexOffset,
        roofLineFirst: offsetWireframeIndex(slice.roofLineFirst, vertexOffset),
      })),
    ],
    cutoutVerticalSlices: [
      ...selected.cutoutVerticalSlices,
      ...sibling.cutoutVerticalSlices.map((slice) => ({
        ...slice,
        first: slice.first + vertexOffset,
      })),
    ],
  };
};
